import { useLocation, useNavigate } from "@remix-run/react";
import { FileCheck } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { useLoginDismissal } from "~/features/baa-nudge/hooks/use-login-dismissal";
import { useUserDetails } from "~/hooks/queries/use-user-details";

// AIDEV-NOTE: DPA v1.2 prompt (UK/EU/AU/Canada, not Quebec; resolveDpaPromptState).
// Optional: "Not now" hides it for this login only, and it never blocks the app.
// No accept here on purpose (compliance, 2026-10-23): an acceptance only binds what the
// reader could see, so the banner opens /practitioner/dpa, where the text, change list,
// province question (Canada) and Accept/DocuSeal live.
export function DpaAcceptBanner() {
    const { data: userDetails } = useUserDetails();
    const location = useLocation();
    const navigate = useNavigate();
    const { dismissed, dismiss } = useLoginDismissal("dpa-v1.2");

    const show = userDetails?.practitionerDpaPrompt?.show ?? false;
    const excludedPath =
        location.pathname.startsWith("/onboarding") ||
        location.pathname === "/practitioner/dpa";

    if (dismissed || excludedPath || !show) return null;

    const review = () => {
        const redirect = `${location.pathname}${location.search}${location.hash}`;
        navigate(`/practitioner/dpa?redirect=${encodeURIComponent(redirect)}`);
    };

    return (
        <Alert
            data-testid="dpa-accept-banner"
            className="flex items-center rounded-none border-x-0 border-t-0 border-brand-forest-200 bg-brand-forest-50 text-brand-forest-900"
        >
            <AlertDescription className="flex w-full flex-wrap items-center justify-between gap-4">
                <span className="flex items-start gap-2.5">
                    <FileCheck className="mt-0.5 size-4 shrink-0 text-brand-forest-600" />
                    <span className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium">
                            Your Data Processing Addendum, Version 1.2, is ready
                            to accept.
                        </span>
                        <span className="text-sm opacity-80">
                            It sets out how we handle your clients&apos;
                            information on your behalf. Version 1.2 adds Canada
                            and changes some terms for the UK, EU, Switzerland,
                            and Australia.
                        </span>
                    </span>
                </span>
                <span className="flex shrink-0 flex-wrap items-center gap-2">
                    <Button
                        size="sm"
                        analyticsId="dpa-banner-review"
                        onClick={review}
                    >
                        Review and accept
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        analyticsId="dpa-banner-dismiss"
                        onClick={dismiss}
                    >
                        Not now
                    </Button>
                </span>
            </AlertDescription>
        </Alert>
    );
}
